interface ILog {
    type: LogType;
    trigger?: string;
    userId?: string;
    message: string;
    object?: any;
    objectRef?: string;
    moment?: string;
}

interface IUserDtu {
    id: string;
    username: string;
    email: string;
    password?: string;
    repeatPassword?: string;
}

interface IFileLookupRequest {
    documentNumber: string;
    folder: string;
    file?: string;
}

interface IEntity {
    id?: string;
    logs: ILog[];
}

interface IMailTemplate extends IEntity {
    name: string;
    subject: string;
    content: string;
    type: MailTemplateType;
}

enum MailTemplateType {
    Quotation = 0,
    Invoicing = 1,
}

interface ICatalogList extends IEntity {
    name: string;
    values: string[];
}

interface IProjectType extends IEntity {
    name: string;
}

interface IWholesalePricing {
    purchasePriceDefaultMeasuringUnit: any;
    wholesaleId: string;
    wholesaleRef: string;
    default: boolean;
    measuringUnit: string;
    brand: string;
    kind: string;
    amount: number;
    purchasePrice: number;
    purchasePricePrivate: number;
    salesPricePrivate: number;
    salesPriceOrganisation: number;
    convertAmountA?: number;
    convertAmountB?: number;
    purchaseAmount?: number;
}

interface IRawMaterial extends IEntity {
    name: string;
    description: string;
    measuringUnit: string;
    margin: number;
    pricing: IWholesalePricing[];
    pricingHistory?: IWholesalePricing[];
    readonly defaultPrice?: IWholesalePricing;
    unused: boolean;
    conversions: IRawMaterialConversion[];
}

interface IRawMaterialConversion {
    amountA: number;
    amountB: number;
    measuringUnitA: string;
    measuringUnitB: string;
}

interface ILocation {
    latitude: number;
    longitude: number;
    accuracy: number;
}

interface IProgressStatement {
    documentNumber: string;
    readonly createDate: string;
    readonly lastModified: string;
    status: number;
    projectId: string;
    projectType: string;
    quotationReference: string;
    contact: IBasicContact | null;
    customer: IBasicContact | null;
    blocks: IProgressStatementBlock[];
    internalNotes: string;
    articles: IArticle[];
    readonly articleSummary: IProgressStatementArticleUsage[];
}

interface IProgressStatementArticleUsage {
    articleId: string;
    variant: string;
    quantity: number;
    unitPrice: number;
}

interface IProgressStatementBlock {
    title: string;
    lines: IProgressStatementLine[];
    readonly totalCost: number;
    readonly totalMargin: number;
    readonly total: number;
}

interface IProgressStatementLine {
    articleId: string;
    variant: string;
    quantity: number;
    description: string;
    remarks: string;
    price: number;
    cost: number;
    originalPrice: number;
    readonly margin: number;
    readonly totalCost: number;
    readonly totalMargin: number;
    readonly total: number;
}

interface IMeasurementUsedArticle {
    // article: IArticle;
    articleId: string;
    height: number;
    length: number;
    amount: number;
    deduction: boolean;
}

interface IMeasurementRoom {
    name: string;
    show?: boolean;
    usedArticles: IMeasurementUsedArticle[];
}

enum MeasurementWorkType {
    Binnenwerk = 0,
    Buitenwerk = 1,
}

interface IMeasurementDuplicateDtu {
    measurementName: string;
    newName: string;
}

enum LogType {
    Info = 0,
    Success = 1,
    Warning = 2,
    Error = 3,
    Note = 4,
    Mail = 5,
    PhoneCall = 6,
}

enum MeasurementStatus {
    open = 0,
    inProgress = 1,
    closed = 2,
    processed = 3,
}

interface IMeasurement {
    name: string;
    documentNumber: string;
    projectId: string;
    status: MeasurementStatus;
    recorder: string;
    date: Date;
    type: MeasurementWorkType;
    workType: string;
    rooms?: IMeasurementRoom[];
    reference: string;
}

enum ProjectStatus {
    open = 0,
    quotationSend = 3,
    accepted = 4,
    declined = 5,
    inProgress = 7,
    completed = 8,
}

interface IProjectStatus extends IEntity {
    readonly value: number;
    name: string;
    status?: ProjectStatus;
}

interface IMeasuringState {
    readonly quotationIds: string[];
    title: string;
    projectId: string;
    subcontractorId?: string;
    team?: string;
    readonly documentNumber: string;
    readonly createDate: string;
    readonly lastModified: string;
    blocks: IMeasuringStateBlock[];
    readonly codeSummary: IMeasuringStateCodeUsage[];
}

interface IMeasuringStateBlock {
    title: string;
    selected: boolean;
    lines: IMeasuringStateLine[];
}

interface IMeasuringStateLine {
    readonly code: string;
    readonly measuringUnit: string;
    readonly variant: string;
    readonly description: string;
    readonly remarks?: string;
    readonly length?: number;
    readonly height?: number;
    readonly amount?: number;
    readonly quantity: number;
    selected: boolean;
}

interface IMeasuringStateCodeUsage {
    readonly code: string;
    readonly variant: string;
    readonly quantity: number;
}



interface IProjectAssignedContact {
    tags: string[];
    contact: IProjectAssignedContactObject;
    sendInvoice: boolean;
    sendQuotation: boolean;
    sendProgressStatement: boolean;
    sendProjectFiches: boolean;
}

interface IProjectVisit {
    date: string;
    employeeId: string;
    completed: boolean;
    pageId: string;
    webUrl: string;
    clientUrl: string;
}

interface IProjectReport {
    date: string;
    employeeId: string;
    completed: boolean;
    pageId: string;
    webUrl: string;
    clientUrl: string;
}

interface IProject extends IEntity {
    readonly name: string;
    readonly projectNumber: string;
    status: number;
    declineReason: string;
    location?: ILocation;
    type: string;
    address?: IAddress;
    customerReference: string | null;
    caseNumberRsz: string | null;
    termsOfPayment: number | null;
    measurements?: IMeasurement[];
    requestedExecutionDate?: string;
    measuringStates?: IMeasuringState[];
    contacts: IProjectAssignedContact[];
    visits: IProjectVisit[];
    reports: IProjectReport[];
    siteLabel?: string | null;
}

interface IProjectStats {
    readonly open: number;
    readonly pipeline: number;
    readonly accepted: number;
    readonly pending: number;
}

enum AddressType {
    Default = 0,
    DeliveryAddress = 1,
    InvoicingAddress = 2,
    VisitAddress = 3,
}

interface IFileMeta {
    name: string;
    uploadTime: string;
    blobName: string;
    contentType: string;
    objectId: string;
    tags: string[];
}

interface IAddress {
    name: string;
    type: AddressType;
    addressline1: string;
    addressline2: string;
    zip: string;
    city: string;
    country: string;
}

interface IBasicContact {
    id: string;
    displayName: string;
    type: string;
    phone: string;
    email: string;
}

// TODO: Cleanup and replace by IBasicContact
interface IProjectAssignedContactObject {
    id: string;
    displayName: string;
    type: string;
    phone: string;
    email: string;
}

// CONTACTS START
interface IContact extends IEntity {
    firstname: string;
    team?: string;
    type: string;
    middlename?: string;
    lastname?: string;
    jobTitle?: string;
    email?: string;
    phone?: string;
    mobile?: string;
    language?: string;
    nationality?: string;
    nationalInsuranceNumber?: string;
    socialSecurityNumber?: string;
    dateOfBirth?: string;
    tags: string[];
    notes?: string;
    readonly displayName: string;
    address?: IAddress;
    userId?: string;
    companyId?: string;
    pricingAgreements: IPricingAgreement[] | null;
}

interface ICompany extends IEntity {
    companyName: string;
    vatNumber?: string;
    email?: string;
    phone?: string;
    language?: string;
    termsOfPayment: number | null;
    contactIds: string[];
    tags: string[];
    notes?: string;
    readonly displayName: string;
    addresses: IAddress[] | null;
    pricingAgreements: IPricingAgreement[] | null;
    type: string;
}

interface IContactRelation {
    contactA: string;
    contactB: string;
    reference: string;
}

interface IContactItem extends IEntity {
    readonly displayName: string;
    readonly phone: string;
    readonly email: string;
    readonly type: number;
    readonly objectId: string;
}

enum ContactCompanyRelationType {
    Subcontractor = 0,
    Company = 1,
}

interface ISubcontractorTeam {
    name: string;
    hourlyRate: number;
}

interface ISubcontractorLabourAgreement {
    articleId: string;
    type: string;
    price: number;
}

interface ISubcontractorLabourAgreementDtu extends ISubcontractorLabourAgreement {
    target: number;
    custom: boolean;
}

interface IPricingAgreement {
    articleId: string;
    projectType: string;
    price: number | null;
    variant: string;
}

interface IPricingAgreementDtu extends IPricingAgreement {
    custom: boolean;
    target: number | null;
}

interface ISubcontractor extends ICompany {
    teams: ISubcontractorTeam[];
    labourAgreements: ISubcontractorLabourAgreement[] | null;
    specialty?: string;
    tunnelGroupsId?: string | null;
    TunnelGroups?: string | null;
}
// CONTACTS END

// QUOTATION START
enum QuotationStatus {
    Draft = 0,
    Open = 1,
    Send = 2,
    Approved = 3,
    Declined = 4,
}

interface IQuotationStats {
    readonly open: number;
    readonly draft: number;
    readonly send: number;
}

interface IQuotation extends IEntity {
    projectId: string;
    documentNumber: string;
    title: string;
    createDate: string;
    date: string | null;
    lastModified: string;
    status: QuotationStatus;
    projectType: string | null;
    measurementName: string | null;
    blocks: IQuotationBlock[];
    articleSummary: IQuotationArticleUsage[];
    articles: IArticle[];
    notes?: string;
    internalNotes?: string;
    customer?: IBasicContact;
    customerContact?: IBasicContact;
    readonly total: number;
    readonly originalTotal: number;
    readonly totalDifference: number;
    readonly margin: number;
}

interface IQuotationBlock {
    showTitle: boolean;
    showTotals: boolean;
    title: string;
    lines: IQuotationLine[];
    readonly total: number;
    readonly originalTotal: number;
    readonly totalDifference: number;
    readonly margin: number;
}

interface IQuotationLine {
    article?: IArticle;
    articleId: string | null;
    measuringUnit: string | null;
    variant: string;
    length: number | null;
    height: number | null;
    amount: number | null;
    quantity: number;
    price: number;
    originalPrice: number;
    cost: number;
    discount: number;
    description: string;
    remarks: string;
    readonly total: number;
    readonly originalTotal: number;
    readonly margin: number;
}

interface IQuotationArticleUsage {
    articleId: string;
    article: IArticle;
    variant: string;
    quantity: number;
    unitPrice: number;
}

// QUOTATION END

// INVOICE START
enum InvoiceStatus {
    Draft = 0,
    Open = 1,
    Send = 2,
    Approved = 3,
    Declined = 4,
}

interface IInvoiceStats {
    readonly open: number;
    readonly draft: number;
    readonly send: number;
}

interface IInvoice extends IEntity {
    projectId: string;
    documentNumber: string;
    projectName: string;
    projectNameNormalized: string;
    projectType: string;
    createDate: string;
    date: string | null;
    lastModified: string;
    status: InvoiceStatus;
    customer?: IBasicContact;
    customerContact?: IBasicContact;
    blocks: IInvoiceBlock[];
    metaData: IMetaDataItem[];
    readonly articleSummary: IInvoiceArticleUsage[];
    articles: IArticle[];
    notes?: string;
    internalNotes?: string;
    readonly total: number;
    readonly originalTotal: number;
    readonly totalDifference: number;
    readonly margin: number;
    advancedInvoice: boolean;
    advancedInvoiceProcessed: boolean;
}

interface IInvoiceBlock {
    showTitle: boolean;
    showTotals: boolean;
    title: string;
    lines: IInvoiceLine[];
    readonly total: number;
    readonly originalTotal: number;
    readonly totalDifference: number;
    readonly margin: number;
}

interface IInvoiceLine {
    articleId?: string;
    variant: string;
    length: number | null;
    height: number | null;
    amount: number | null;
    quantity: number;
    price: number;
    originalPrice: number;
    cost: number;
    discount: number;
    description: string;
    remarks: string;
    readonly total: number;
    readonly originalTotal: number;
    readonly margin: number;
}

interface IInvoiceArticleUsage {
    article: IArticle;
    variant: string;
    quantity: number;
    unitPrice: number;
}

// INVOICE END

interface IMetaDataItem {
    key: string;
    value: string;
}

type TypeOrNull<T> = T | null;
